import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabGroup, TabButton } from '../../ui/Tabs/index.js';
import { extractDateString } from '../../../lib/date-utils.js';
import { formatPt } from '../../../lib/format.js';
import { BAR_MAX_SIZE, BAR_RADIUS_TOP } from './chart-styles.js';
import { BugDetectionScatterChart } from './BugDetectionScatterChart.js';
import type { BugDetectionPoint } from '../../../hooks/useBugDetectionDistribution.js';
import { DevelopPointMtdCard } from './DevelopPointMtdCard.js';
import type { DevelopPointView } from './develop-point-view.js';
import type { KpiTeam, KpiTrendResponse } from '../../../types/api.js';

/** バグ率カードの表示ビュー: 'bugRate' = 100ptあたりバグ発生率、'detectionDays' = バグ検知日数分布 */
type BugCardView = 'bugRate' | 'detectionDays';

interface KpiTrendChartsProps {
  /** KPIトレンドAPIレスポンス */
  trendData?: KpiTrendResponse;
  /** トレンドチャート読み込み状態 */
  isTrendLoading: boolean;
  /** チャート説明文 */
  trendDescription: string;
  /** チャートクリック時の詳細モーダル表示コールバック */
  onOpenDetail: (
    date: unknown,
    periodType?: '90d' | 'mtd',
    chartType?: 'bugRate' | 'releasePoints' | 'developPoints' | 'releaseCount',
  ) => void;
  /** バグ検知日数分布チャートのスポットクリック時の詳細モーダル表示コールバック（クリックされた発見日を渡す） */
  onOpenBugDetectionDetail: (date: unknown) => void;
  /** バグ検知日数分布チャートの個別スポット（検知日数 > 0 のバグ 1 件 = 1 点） */
  bugDetectionPoints: BugDetectionPoint[];
  /** 上部の team フィルタ。開発pt カードのビュー切替の表示可否判定に使う */
  team?: KpiTeam | 'all';
  /** 開発pt のビュー（週次pt集計カードと共有）。未指定なら開発pt カードが自前で持つ */
  developPointView?: DevelopPointView;
  /** 開発pt のビュー変更コールバック */
  onDevelopPointViewChange?: (view: DevelopPointView) => void;
}

interface ChartClickState {
  activeLabel?: string | number;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPIダッシュボードの4種トレンドチャート。バグ率、リリースPT、開発PT、リリース件数を表示し、クリックされた日付を詳細モーダルへ連携する。バグ率カードはトグルで「バグ検知日数分布」ビュー（案件起因バグ1件=1スポットで発見日−起因案件リリース日の差分日数をY軸0〜360日に散布、7日以内は健全な青・8〜360日は黄→赤グラデーション・7日に水色の健全ライン、クリックで詳細モーダル）に切替可能。開発PTカードは DevelopPointMtdCard に委譲し、PDG 系スコープでは 全部 / 要件定義抜き / 内訳 の 3 ビューを切り替えられる
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects ComposedChart [calls] KPIトレンド複合チャート描画
 * @graph-connects TabGroup [calls] バグ率カードのビュー切替スイッチ描画
 * @graph-connects DevelopPointMtdCard [calls] 開発pt (MTD) カード描画（ビュー切替 + 内訳ビューの処理中pt 込み）
 * @graph-connects extractDateString [calls] 日付文字列正規化
 */
export function KpiTrendCharts({
  trendData,
  isTrendLoading,
  trendDescription,
  onOpenDetail,
  onOpenBugDetectionDetail,
  bugDetectionPoints,
  team,
  developPointView,
  onDevelopPointViewChange,
}: KpiTrendChartsProps) {
  const { t } = useTranslation();

  // バグ率カードの表示ビュー切替（バグ発生率 ⇄ バグ検知日数分布）
  const [bugCardView, setBugCardView] = useState<BugCardView>('bugRate');

  const hasTrendData = Boolean(trendData?.data && trendData.data.length > 0);
  const handleChartClick =
    (
      periodType: '90d' | 'mtd' = '90d',
      chartType: 'bugRate' | 'releasePoints' | 'developPoints' | 'releaseCount' = 'bugRate',
    ) =>
    (state: ChartClickState) => {
      const date = state.activeLabel;
      if (date != null) {
        onOpenDetail(date, periodType, chartType);
      }
    };
  const renderNoData = () => (
    <div className="h-48 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
      <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
    </div>
  );
  const renderLoading = () => (
    <div className="h-48 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
  );
  const tickFormatter = (value: string) => {
    const dateStr = extractDateString(value);
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return `${String(date.getMonth() + 1)}/${String(date.getDate())}`;
  };
  const labelFormatter = (label: string) => {
    const dateStr = extractDateString(label);
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString();
  };
  const tooltipStyle = {
    backgroundColor: 'var(--color-bg-elevated)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg)',
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="animate-content-reveal">
        <CardHeader
          title={
            bugCardView === 'detectionDays'
              ? t('kpi.bugDetectionDaysDistribution')
              : t('kpi.bugRatePer100pt')
          }
          description={
            bugCardView === 'detectionDays'
              ? t('kpi.bugDetectionDaysDistributionDescription')
              : trendDescription
          }
          action={
            <TabGroup ariaLabel={t('kpi.bugCardViewSwitch')} shape="pill">
              <TabButton
                active={bugCardView === 'bugRate'}
                label={t('kpi.bugRate')}
                onClick={() => {
                  setBugCardView('bugRate');
                }}
              />
              <TabButton
                active={bugCardView === 'detectionDays'}
                label={t('kpi.bugDetectionDaysShort')}
                onClick={() => {
                  setBugCardView('detectionDays');
                }}
              />
            </TabGroup>
          }
        />
        <CardContent>
          {isTrendLoading ? (
            renderLoading()
          ) : hasTrendData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                {bugCardView === 'detectionDays' ? (
                  <BugDetectionScatterChart
                    points={bugDetectionPoints}
                    onPointClick={onOpenBugDetectionDetail}
                  />
                ) : (
                  <ComposedChart
                    data={trendData?.data.map((point) => ({
                      date: point.date,
                      bugRate: point.bugRate != null ? Math.round(point.bugRate * 1000) / 10 : null,
                      bugRateLevel2Plus:
                        point.bugRateLevel2Plus != null
                          ? Math.round(point.bugRateLevel2Plus * 1000) / 10
                          : null,
                    }))}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    onClick={handleChartClick() as never}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={tickFormatter}
                    />
                    <YAxis
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(value: number) => `${String(value)}%`}
                    />
                    <Tooltip
                      position={{ x: 0, y: 0 }}
                      wrapperStyle={{
                        top: 0,
                        right: 20,
                        left: 'auto',
                        transform: 'translateY(-100%)',
                        zIndex: 50,
                      }}
                      contentStyle={tooltipStyle}
                      labelStyle={{ color: 'var(--color-text-primary)' }}
                      formatter={
                        ((value: number, name: string) => {
                          const label =
                            name === 'bugRate' ? t('kpi.bugRate') : t('kpi.bugRateLevel2Plus');
                          return [`${String(value)}%`, label];
                        }) as never
                      }
                      labelFormatter={labelFormatter as never}
                    />
                    <Line
                      type="monotone"
                      dataKey="bugRate"
                      name="bugRate"
                      stroke="var(--color-accent-primary)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: 'var(--color-accent-primary)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="bugRateLevel2Plus"
                      name="bugRateLevel2Plus"
                      stroke="var(--color-error)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: 'var(--color-error)' }}
                    />
                    <ReferenceLine
                      y={15}
                      stroke="#7dd3fc"
                      strokeWidth={1.5}
                      strokeDasharray="5 3"
                      ifOverflow="extendDomain"
                    />
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            </div>
          ) : (
            renderNoData()
          )}
        </CardContent>
      </Card>

      <Card className="animate-content-reveal">
        <CardHeader title={t('kpi.releasePoint90d')} description={trendDescription} />
        <CardContent>
          {isTrendLoading ? (
            renderLoading()
          ) : hasTrendData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trendData?.data.map((point) => ({
                    date: point.date,
                    value: point.releasePoint90d,
                  }))}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  onClick={handleChartClick('90d', 'releasePoints') as never}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="date"
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    tickFormatter={tickFormatter}
                  />
                  <YAxis
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    tickFormatter={(value: number) => String(value)}
                  />
                  <Tooltip
                    position={{ x: 0, y: 0 }}
                    wrapperStyle={{
                      top: 0,
                      right: 20,
                      left: 'auto',
                      transform: 'translateY(-100%)',
                      zIndex: 50,
                    }}
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    itemStyle={{ color: 'var(--color-info)' }}
                    formatter={
                      ((value: number) => [
                        `${formatPt(value ?? 0)}pt`,
                        t('kpi.releasePoint90d'),
                      ]) as never
                    }
                    labelFormatter={labelFormatter as never}
                  />
                  <Bar
                    dataKey="value"
                    fill="var(--color-info-muted)"
                    stroke="var(--color-info)"
                    strokeWidth={1}
                    maxBarSize={BAR_MAX_SIZE}
                    radius={BAR_RADIUS_TOP}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            renderNoData()
          )}
        </CardContent>
      </Card>

      <DevelopPointMtdCard
        points={trendData?.data ?? []}
        isLoading={isTrendLoading}
        description={trendDescription}
        onOpenDetail={handleChartClick('mtd', 'developPoints')}
        tickFormatter={tickFormatter}
        labelFormatter={labelFormatter}
        tooltipStyle={tooltipStyle}
        team={team}
        view={developPointView}
        onViewChange={onDevelopPointViewChange}
        wip={trendData?.wip}
      />

      <Card className="animate-content-reveal">
        <CardHeader title={t('kpi.releaseCountMtd')} description={trendDescription} />
        <CardContent>
          {isTrendLoading ? (
            renderLoading()
          ) : hasTrendData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trendData?.data.map((point) => ({
                    date: point.date,
                    value: point.releaseCountMtd,
                    target: point.releaseCountMtdTarget,
                  }))}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  onClick={handleChartClick('mtd', 'releaseCount') as never}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="date"
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    tickFormatter={tickFormatter}
                  />
                  <YAxis
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    tickFormatter={(value: number) => String(value)}
                  />
                  <Tooltip
                    position={{ x: 0, y: 0 }}
                    wrapperStyle={{
                      top: 0,
                      right: 20,
                      left: 'auto',
                      transform: 'translateY(-100%)',
                      zIndex: 50,
                    }}
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    formatter={
                      ((value: number, name: string) => {
                        if (name === 'target') {
                          return [(value ?? 0).toLocaleString(), t('kpi.releaseCountMtdTarget')];
                        }
                        return [(value ?? 0).toLocaleString(), t('kpi.releaseCountMtd')];
                      }) as never
                    }
                    labelFormatter={labelFormatter as never}
                  />
                  <Bar
                    dataKey="value"
                    name="value"
                    fill="var(--color-warning)"
                    fillOpacity={0.25}
                    stroke="var(--color-warning)"
                    strokeWidth={1}
                    maxBarSize={BAR_MAX_SIZE}
                    radius={BAR_RADIUS_TOP}
                  />
                  <Line
                    dataKey="target"
                    name="target"
                    stroke="#7dd3fc"
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="5 3"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            renderNoData()
          )}
        </CardContent>
      </Card>
    </div>
  );
}
